import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Plus, ShieldAlert, Trash2, UserPlus } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { areaClass, fieldClass } from '../../../components/ui/form-classes';
import {
  assignedSoldierIds,
  byId,
  newExternalPassengerId,
  normalizeTeam,
  upsertTeam,
  validateTaskTeams,
} from '../../../core/assignment-rules';
import { describeExternalPersonRecord, temporaryVehicleLabel } from '../../../core/external-people';
import { useExternalPeople } from './use-external-people';
import { SAVE_EXTERNAL_PERSON_LABEL } from '../../../types/external-person';
import {
  CLEAR_ASSIGNMENT_MESSAGE,
  VEHICLE_AVAILABILITY_LEGEND,
  checkTeamAssignment,
  isOverridden,
  pruneOverrides,
  unresolvedConflicts,
  vehicleAvailabilityLights,
} from '../../../core/conflict-checker';
import type { ScheduleEntry, TaskWindow } from '../../../core/conflict-checker';
import {
  CONFLICT_SEVERITY_LABELS,
  CONFLICT_SEVERITY_TONES,
  CREW_ROLES,
  CREW_ROLE_LABELS,
  DEFAULT_ESCORT_OPTION,
  ESCORT_OPTIONS,
  ESCORT_OPTION_LABELS,
  EXTERNAL_PERSON_TAG,
  EXTERNAL_PERSON_TONE,
  SCHEDULE_CONFLICT_TYPE_LABELS,
  TEMPORARY_VEHICLE_LABEL,
  describeOverrideReason,
} from '../../../types/assignment';
import type {
  AssignmentOverride,
  CrewRole,
  EscortOption,
  ExternalPersonDraft,
  TaskTeam,
} from '../../../types/assignment';
import { vehicleCallSign } from '../../../core/military-id';
import { VEHICLE_TYPE_LABELS } from '../../../types/fleet';
import type { Soldier, Vehicle } from '../../../types/fleet';
import type { User } from '../../../types/user';
import { OverrideDialog } from './OverrideDialog';

interface AddTeamModalProps {
  taskId: string;
  /** The task's other teams — what keeps a soldier bound to one vehicle. */
  teams: readonly TaskTeam[];
  /** `null` opens the modal empty, to add a vehicle to the task. */
  team: TaskTeam | null;
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
  /** The task's own time window — without it, nothing is checked against time. */
  task?: TaskWindow | null;
  /** Every other open task with a convoy, for the time-overlap check. */
  schedule?: readonly ScheduleEntry[];
  /** The `User.id` that signs an override. */
  managerId?: string;
  /**
   * The signed-in operator. Only needed to *keep* an external person under
   * אנשי חוץ — without one the form still places them, they simply stay inside
   * this mission.
   */
  user?: User | null;
  onClose: () => void;
  onSave: (team: TaskTeam) => void | Promise<void>;
}

/** The three seats an external person may be placed in — the same three roles. */
const EXTERNAL_ROLE_OPTIONS: readonly CrewRole[] = CREW_ROLES;

function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-ink/60 mb-1">{label}</span>
      {children}
    </label>
  );
}

/** The soldier line inside a dropdown: name, personal number, platoon. */
const soldierOption = (soldier: Soldier): string =>
  `${soldier.fullName} — ${soldier.personalNumber}${soldier.platoon ? ` (${soldier.platoon})` : ''}`;

/**
 * A seat held by somebody off the roster — the driver's or the commander's.
 * Shown in place of the soldier selector, marked "איש חוץ", and removable in one
 * tap so the seat goes straight back to the roster.
 */
function ExternalSeat({
  person,
  seatLabel,
  onRemove,
}: {
  person: ExternalPersonDraft;
  seatLabel: string;
  onRemove: () => void;
}) {
  const vehicle = temporaryVehicleLabel(person);
  return (
    <div
      className={`flex items-start justify-between gap-2 rounded-xl border px-3 py-2 text-sm ${EXTERNAL_PERSON_TONE}`}
    >
      <div className="min-w-0 space-y-0.5">
        <p className="font-bold truncate">
          <span className="font-normal opacity-70">{seatLabel}: </span>
          {person.fullName}
        </p>
        {person.phone || person.personalNumber ? (
          <p data-dir="ltr" className="text-xs tabular-nums opacity-80">
            {[person.phone, person.personalNumber].filter(Boolean).join(' · ')}
          </p>
        ) : null}
        {person.role ? <p className="text-xs opacity-70">{person.role}</p> : null}
        {vehicle ? (
          <p className="text-xs font-medium">
            {TEMPORARY_VEHICLE_LABEL}: {vehicle}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onRemove}
        title={`הסרת ${EXTERNAL_PERSON_TAG} מהתפקיד`}
        className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-rose-600 hover:bg-rose-50"
      >
        <Trash2 size={14} aria-hidden />
      </button>
    </div>
  );
}

/**
 * Add or edit one vehicle team on a task.
 *
 * Everything the modal collects hangs off the vehicle chosen at the top: the
 * driver, the escort decision and every passenger are saved as crew bound to
 * that vehicle. A soldier already riding with another vehicle on this task is
 * not offered again — the exclusivity rule is asked of
 * `src/core/assignment-rules.ts`, never re-decided here.
 *
 * Every choice is checked as it is made, against the licence engine and the
 * board's own schedule (`src/core/conflict-checker.ts`). A candidate that is
 * unlicensed, away, or already out on an overlapping task is still offered —
 * the form warns with the exact details and requires the manager to take
 * responsibility through "שבץ בכל זאת" instead of hiding the option.
 */
export function AddTeamModal({
  taskId,
  teams,
  team,
  vehicles,
  soldiers,
  task,
  schedule,
  managerId = '',
  user,
  onClose,
  onSave,
}: AddTeamModalProps) {
  const [vehicleId, setVehicleId] = useState(team?.vehicleId ?? '');
  const [driverId, setDriverId] = useState(team?.driverId ?? '');
  const [escortOption, setEscortOption] = useState<EscortOption>(
    team?.escortOption ?? DEFAULT_ESCORT_OPTION,
  );
  const [escortId, setEscortId] = useState(team?.escortId ?? '');
  const [passengerIds, setPassengerIds] = useState<string[]>(() =>
    (team?.passengers ?? []).flatMap((passenger) =>
      passenger.soldierId ? [passenger.soldierId] : [],
    ),
  );
  const [externals, setExternals] = useState<ExternalPersonDraft[]>(() =>
    (team?.passengers ?? []).flatMap((passenger) =>
      passenger.externalPassenger ? [{ ...passenger.externalPassenger }] : [],
    ),
  );
  /** The two seats an external person can hold besides a passenger's. */
  const [externalDriver, setExternalDriver] = useState<ExternalPersonDraft | null>(
    team?.externalDriver ? { ...team.externalDriver } : null,
  );
  const [externalEscort, setExternalEscort] = useState<ExternalPersonDraft | null>(
    team?.externalEscort ? { ...team.externalEscort } : null,
  );
  const [teamNote, setTeamNote] = useState(team?.teamNote ?? '');

  /** אנשי חוץ already on file — the list the form offers before asking to type. */
  const directory = useExternalPeople(user);

  const [externalOpen, setExternalOpen] = useState(false);
  const [externalName, setExternalName] = useState('');
  const [externalNumber, setExternalNumber] = useState('');
  const [externalPhone, setExternalPhone] = useState('');
  const [externalRole, setExternalRole] = useState('');
  /** The seat this external person is being placed in. */
  const [externalCrewRole, setExternalCrewRole] = useState<CrewRole>('passenger');
  const [externalModel, setExternalModel] = useState('');
  const [externalPlate, setExternalPlate] = useState('');
  /** "שמור כאיש חוץ לפעמים הבאות" — off by default, as the requirement reads. */
  const [externalKeep, setExternalKeep] = useState(false);
  /** The card this person was taken from, when they came off the directory. */
  const [externalDirectoryId, setExternalDirectoryId] = useState('');
  /**
   * The ids of the people this form was told to keep. Flushed to אנשי חוץ only
   * once the team itself saves — a manager who backs out of the dialog has not
   * added anybody to the directory either.
   */
  const [keepIds, setKeepIds] = useState<string[]>([]);

  /** Warnings this manager has already taken responsibility for. */
  const [overrides, setOverrides] = useState<AssignmentOverride[]>(() => [
    ...(team?.overrides ?? []),
  ]);
  const [overrideOpen, setOverrideOpen] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /** Fixed evaluation moment, so licence expiry does not drift mid-edit. */
  const [now] = useState(() => Date.now());

  /** Soldiers and vehicles already spoken for by the task's other teams. */
  const taken = assignedSoldierIds(teams, team?.id);
  const takenVehicles = new Set(
    teams.flatMap((current) => (current.id === team?.id ? [] : [current.vehicleId])),
  );

  const availableVehicles = useMemo(
    () => vehicles.filter((vehicle) => vehicle.id === vehicleId || !takenVehicles.has(vehicle.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `takenVehicles` is derived from `teams`
    [vehicles, teams, team?.id, vehicleId],
  );
  const availableSoldiers = soldiers.filter((soldier) => !taken.has(soldier.id));
  const chosenVehicle = byId(vehicles, vehicleId);

  /**
   * The automated check, re-run on every choice: the matching engine for the
   * driver's licence, the schedule for time overlaps, the roster for a status
   * that has not expired yet.
   */
  const check = useMemo(
    () =>
      checkTeamAssignment(
        {
          vehicleId,
          driverId,
          escortId: escortOption === 'soldier' ? escortId : undefined,
          passengerIds,
        },
        { vehicles, soldiers, task, schedule, exceptTeamId: team?.id, now },
      ),
    [vehicleId, driverId, escortOption, escortId, passengerIds, vehicles, soldiers, task, schedule, team?.id, now],
  );

  /**
   * The availability light of every vehicle on offer, for the window the task
   * is asking for — green when nobody else has it, red when it is already out
   * on an overlapping task. A red vehicle is still selectable: the light is
   * there to save the manager from finding out only after they chose, and the
   * non-blocking warning banner below is what still asks them to confirm.
   */
  const availability = useMemo(
    () => vehicleAvailabilityLights(availableVehicles, { task, schedule, exceptTeamId: team?.id, now }),
    [availableVehicles, task, schedule, team?.id, now],
  );

  /** The light of the vehicle actually chosen — spelled out under the selector. */
  const chosenLight = vehicleId ? availability.get(vehicleId) ?? null : null;

  /** Overrides only count while their warning is still on the board. */
  const liveOverrides = pruneOverrides(overrides, check.conflicts);
  const openConflicts = unresolvedConflicts(check.conflicts, liveOverrides);
  const checked = Boolean(
    vehicleId ||
      driverId ||
      escortId ||
      passengerIds.length > 0 ||
      externals.length > 0 ||
      externalDriver ||
      externalEscort,
  );

  const togglePassenger = (soldierId: string) => {
    setPassengerIds((current) =>
      current.includes(soldierId)
        ? current.filter((id) => id !== soldierId)
        : [...current, soldierId],
    );
  };

  /** Opens the external form, on a clean slate, for one seat. */
  const openExternalForm = (crewRole: CrewRole) => {
    setExternalName('');
    setExternalNumber('');
    setExternalPhone('');
    setExternalRole('');
    setExternalModel('');
    setExternalPlate('');
    setExternalKeep(false);
    setExternalDirectoryId('');
    setExternalCrewRole(crewRole);
    setExternalOpen(true);
  };

  /** Fills the form from a card already on file, so nothing is re-typed. */
  const fillFromDirectory = (recordId: string) => {
    setExternalDirectoryId(recordId);
    const record = directory.people.find((entry) => entry.id === recordId);
    if (!record) return;
    setExternalName(record.fullName);
    setExternalNumber(record.personalNumber ?? '');
    setExternalPhone(record.phone ?? '');
    setExternalRole(record.role ?? '');
    setExternalModel(record.vehicleModel ?? '');
    setExternalPlate(record.plateNumber ?? '');
    if (record.defaultCrewRole) setExternalCrewRole(record.defaultCrewRole);
  };

  /**
   * Places the typed-in person in the seat that was chosen for them.
   *
   * A name is the one field the form insists on: it is what every reading of the
   * order — the card, the sheet, the shared image — names them by. Everything
   * else is optional, because a technician met at the gate often comes with a
   * phone and nothing more.
   */
  const addExternalPerson = () => {
    if (!externalName.trim()) {
      setError('לאיש חוץ נדרש שם מלא.');
      return;
    }
    setError(null);
    const person: ExternalPersonDraft = {
      id: newExternalPassengerId(),
      fullName: externalName,
      personalNumber: externalNumber,
      phone: externalPhone,
      role: externalRole,
      vehicleModel: externalModel,
      plateNumber: externalPlate,
      directoryId: externalDirectoryId || undefined,
    };

    if (externalCrewRole === 'driver') {
      // One wheel, one pair of hands: naming an external driver releases the
      // roster driver rather than leaving the team with two.
      setDriverId('');
      setExternalDriver(person);
    } else if (externalCrewRole === 'escort') {
      setEscortOption('external');
      setEscortId('');
      setExternalEscort(person);
    } else {
      setExternals((current) => [...current, person]);
    }

    if (externalKeep && person.id) setKeepIds((current) => [...current, person.id as string]);
    setExternalOpen(false);
  };

  const handleSubmit = async () => {
    setError(null);

    // A warning never blocks the assignment — an *unacknowledged* one does.
    if (openConflicts.length > 0) {
      setError(
        `נמצאו ${openConflicts.length} התראות שיבוץ שלא אושרו. יש ללחוץ "שבץ בכל זאת" ולבחור סיבה, או לשנות את הבחירה.`,
      );
      return;
    }

    const candidate = normalizeTeam(
      {
        id: team?.id,
        vehicleId,
        driverId,
        externalDriver: externalDriver ?? undefined,
        escortOption,
        escortId,
        externalEscort: externalEscort ?? undefined,
        passengers: [
          ...passengerIds.map((soldierId) => ({ soldierId })),
          ...externals.map((externalPassenger) => ({ externalPassenger })),
        ],
        teamNote,
        overrides: liveOverrides,
      },
      { taskId, existing: team },
    );

    const conflicts = validateTaskTeams(upsertTeam(teams, candidate));
    if (conflicts.length > 0) {
      setError(conflicts.map((item) => item.message).join(' • '));
      return;
    }

    setSaving(true);
    try {
      await onSave(candidate);
      // Only now, once the convoy itself landed, is anybody filed under
      // אנשי חוץ — and only those the manager ticked the box for. Everyone else
      // stays inside this mission and nowhere else, which is the whole rule.
      await keepTickedPeople(candidate);
      onClose();
    } catch (err) {
      setError(`השמירה נכשלה — ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Writes the ticked people into the directory. A failure here is reported but
   * never undoes the assignment: the convoy is the operational fact, the address
   * book is a convenience.
   */
  const keepTickedPeople = async (saved: TaskTeam): Promise<void> => {
    if (keepIds.length === 0) return;
    const wanted = new Set(keepIds);
    const seats: { person: ExternalPersonDraft; crewRole: CrewRole }[] = [
      ...(saved.externalDriver ? [{ person: saved.externalDriver, crewRole: 'driver' as CrewRole }] : []),
      ...(saved.externalEscort ? [{ person: saved.externalEscort, crewRole: 'escort' as CrewRole }] : []),
      ...saved.passengers.flatMap((passenger) =>
        passenger.externalPassenger
          ? [{ person: passenger.externalPassenger, crewRole: 'passenger' as CrewRole }]
          : [],
      ),
    ];
    for (const seat of seats) {
      if (!seat.person.id || !wanted.has(seat.person.id)) continue;
      await directory.remember(seat.person, seat.crewRole);
    }
  };

  const statusBanner = !checked ? null : !check.hasConflict ? (
    <p
      role="status"
      className="text-sm rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-700 p-3 flex items-center gap-2"
    >
      <CheckCircle2 size={16} className="shrink-0" aria-hidden />
      {CLEAR_ASSIGNMENT_MESSAGE}
    </p>
  ) : (
    <section
      role="status"
      className="rounded-2xl border border-amber-300 bg-amber-50 p-3 space-y-2 text-amber-900"
    >
      <h3 className="text-xs font-bold flex items-center gap-1.5">
        <ShieldAlert size={14} aria-hidden />
        נמצאו {check.conflicts.length} התראות שיבוץ — הבחירה אינה נחסמת
      </h3>
      <ul className="space-y-1.5">
        {check.conflicts.map((conflict) => {
          const acknowledged = isOverridden(conflict, liveOverrides);
          const override = liveOverrides.find((item) => item.conflictKey === conflict.key);
          return (
            <li
              key={conflict.key}
              className="rounded-xl border border-line bg-panel p-2 text-sm text-ink/80 flex items-start gap-2"
            >
              {acknowledged ? (
                <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600" aria-hidden />
              ) : (
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" aria-hidden />
              )}
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-ink/50">
                    {SCHEDULE_CONFLICT_TYPE_LABELS[conflict.conflictType]}
                  </span>
                  <span
                    className={`text-[11px] px-1.5 py-0.5 rounded-lg border ${CONFLICT_SEVERITY_TONES[conflict.severity]}`}
                  >
                    {CONFLICT_SEVERITY_LABELS[conflict.severity]}
                  </span>
                  {conflict.conflictingWindow ? (
                    <span
                      data-dir="ltr"
                      className="text-[11px] px-1.5 py-0.5 rounded-lg border border-line bg-canvas tabular-nums"
                    >
                      {conflict.conflictingWindow}
                    </span>
                  ) : null}
                </div>
                <p>{conflict.details}</p>
                {acknowledged && override ? (
                  <p className="text-xs text-emerald-700">
                    שובץ בכל זאת — {describeOverrideReason(override)}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      {openConflicts.length > 0 ? (
        <button
          type="button"
          onClick={() => setOverrideOpen(true)}
          className="h-9 px-4 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 flex items-center gap-1.5"
        >
          <ShieldAlert size={15} aria-hidden />
          שבץ בכל זאת
        </button>
      ) : (
        <p className="text-xs text-emerald-700 flex items-center gap-1.5">
          <CheckCircle2 size={13} aria-hidden />
          כל ההתראות אושרו ויירשמו ביומן הביקורת בעת השמירה.
        </p>
      )}
    </section>
  );

  return (
    <>
    <Modal
      title={team ? 'עריכת צוות ורכב' : 'הוספת רכב וצוות'}
      subtitle="הנהג, מפקד המשימה והנוסעים משויכים לרכב שנבחר כאן בלבד"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving}
            className="h-10 px-5 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? 'שומר…' : team ? 'שמירת הצוות' : 'הוספת הצוות'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {statusBanner}

        <section className="rounded-2xl border border-line p-3 space-y-3">
          <h3 className="text-xs font-bold text-ink/50">הרכב</h3>
          <div className="flex items-end gap-2">
            <div className="grow min-w-0">
              <Labelled label="בחירת רכב">
                <select
                  value={vehicleId}
                  onChange={(event) => setVehicleId(event.target.value)}
                  aria-label="בחירת רכב"
                  className={fieldClass}
                >
                  <option value="">— בחר רכב —</option>
                  {availableVehicles.map((vehicle) => {
                    // The light rides inside the option text: a native <option>
                    // renders no markup, and a dot the operating system draws
                    // itself is legible on every phone the unit carries.
                    const light = availability.get(vehicle.id);
                    return (
                      <option key={vehicle.id} value={vehicle.id} title={light?.label}>
                        {light?.dot ?? ''} {vehicle.name} — {vehicleCallSign(vehicle)} ·{' '}
                        {VEHICLE_TYPE_LABELS[vehicle.vehicleType]}
                      </option>
                    );
                  })}
                </select>
              </Labelled>
            </div>
            <span
              data-dir="ltr"
              title="מספר פנימי"
              className="shrink-0 h-10 px-3 rounded-xl border border-brand-300 bg-brand-50 text-brand-700 font-bold tabular-nums flex items-center"
            >
              {chosenVehicle ? vehicleCallSign(chosenVehicle) : '—'}
            </span>
          </div>

          <p className="text-[11px] text-ink/50">{VEHICLE_AVAILABILITY_LEGEND}</p>

          {chosenLight ? (
            <p
              role="status"
              className={[
                'text-xs rounded-xl border p-2 flex items-start gap-1.5',
                chosenLight.state === 'free'
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  : 'border-rose-300 bg-rose-50 text-rose-700',
              ].join(' ')}
            >
              <span aria-hidden>{chosenLight.dot}</span>
              <span className="min-w-0">{chosenLight.label}</span>
            </p>
          ) : null}
        </section>

        <section className="rounded-2xl border border-line p-3 space-y-3">
          <h3 className="text-xs font-bold text-ink/50">נהג</h3>
          {externalDriver ? (
            <ExternalSeat
              person={externalDriver}
              seatLabel={CREW_ROLE_LABELS.driver}
              onRemove={() => setExternalDriver(null)}
            />
          ) : (
            <Labelled label="נהג הרכב">
              <select
                value={driverId}
                onChange={(event) => setDriverId(event.target.value)}
                aria-label="נהג הרכב"
                className={fieldClass}
              >
                <option value="">— טרם נקבע נהג —</option>
                {availableSoldiers.map((soldier) => (
                  <option key={soldier.id} value={soldier.id}>
                    {soldierOption(soldier)}
                  </option>
                ))}
              </select>
            </Labelled>
          )}
        </section>

        <section className="rounded-2xl border border-line p-3 space-y-3">
          <h3 className="text-xs font-bold text-ink/50">{CREW_ROLE_LABELS.escort}</h3>
          <div className="flex flex-wrap gap-1.5">
            {ESCORT_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setEscortOption(option)}
                aria-pressed={escortOption === option}
                className={[
                  'text-xs px-3 py-1.5 rounded-lg border transition-colors',
                  escortOption === option
                    ? 'border-brand-500 bg-brand-50 text-brand-700 font-bold'
                    : 'border-line bg-canvas text-ink/60',
                ].join(' ')}
              >
                {ESCORT_OPTION_LABELS[option]}
              </button>
            ))}
          </div>

          {escortOption === 'soldier' ? (
            <Labelled label="החייל המשובץ כמפקד משימה">
              <select
                value={escortId}
                onChange={(event) => setEscortId(event.target.value)}
                aria-label="החייל המשובץ כמפקד משימה"
                className={fieldClass}
              >
                <option value="">— בחר מפקד משימה —</option>
                {availableSoldiers.map((soldier) => (
                  <option key={soldier.id} value={soldier.id}>
                    {soldierOption(soldier)}
                  </option>
                ))}
              </select>
            </Labelled>
          ) : null}

          {escortOption === 'external' ? (
            externalEscort ? (
              <ExternalSeat
                person={externalEscort}
                seatLabel={CREW_ROLE_LABELS.escort}
                onRemove={() => {
                  setExternalEscort(null);
                  setEscortOption(DEFAULT_ESCORT_OPTION);
                }}
              />
            ) : (
              <button
                type="button"
                onClick={() => openExternalForm('escort')}
                className="h-9 px-3 rounded-xl border border-violet-300 bg-violet-50 text-sm text-violet-700 hover:bg-violet-100 flex items-center gap-1.5"
              >
                <UserPlus size={15} aria-hidden />
                הזנת פרטי {EXTERNAL_PERSON_TAG} כמפקד משימה
              </button>
            )
          ) : null}
        </section>

        <section className="rounded-2xl border border-line p-3 space-y-3">
          <h3 className="text-xs font-bold text-ink/50">נוסעים</h3>

          <div className="max-h-44 overflow-y-auto rounded-xl border border-line divide-y divide-line">
            {availableSoldiers.length === 0 ? (
              <p className="p-3 text-sm text-ink/40">כל החיילים כבר משובצים לרכבים אחרים במשימה.</p>
            ) : (
              availableSoldiers.map((soldier) => (
                <label
                  key={soldier.id}
                  className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-canvas"
                >
                  <input
                    type="checkbox"
                    checked={passengerIds.includes(soldier.id)}
                    onChange={() => togglePassenger(soldier.id)}
                    className="w-4 h-4 accent-brand-500"
                  />
                  <span className="truncate">{soldier.fullName}</span>
                  <span data-dir="ltr" className="text-xs text-ink/40 tabular-nums">
                    {soldier.personalNumber}
                  </span>
                </label>
              ))
            )}
          </div>

          {externals.length > 0 ? (
            <ul className="space-y-1.5">
              {externals.map((external) => (
                <li
                  key={external.id}
                  className={`flex items-center justify-between gap-2 rounded-xl border px-2 py-1.5 text-sm ${EXTERNAL_PERSON_TONE}`}
                >
                  <span className="min-w-0 truncate">
                    {external.fullName}
                    {external.personalNumber ? ` — ${external.personalNumber}` : ''}
                    {external.phone ? ` · ${external.phone}` : ''}
                    {external.role ? ` · ${external.role}` : ''}
                    {temporaryVehicleLabel(external)
                      ? ` · ${TEMPORARY_VEHICLE_LABEL}: ${temporaryVehicleLabel(external)}`
                      : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setExternals((current) =>
                        current.filter((item) => item.id !== external.id),
                      )
                    }
                    title={`הסרת ${EXTERNAL_PERSON_TAG}`}
                    className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-rose-600 hover:bg-rose-50"
                  >
                    <Trash2 size={14} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </section>

        {/* ── איש חוץ / זמני ─────────────────────────────────────────── */}
        <section className="rounded-2xl border border-violet-300 bg-violet-50/40 p-3 space-y-3">
          <h3 className="text-xs font-bold text-violet-800">אנשי חוץ ורכבים זמניים</h3>
          <p className="text-[11px] text-ink/50">
            מי שאינו במצבת היחידה — נהג מפלוגה אחרת, קבלן, טכנאי. הוא משובץ למשימה זו בלבד, אלא אם
            תסמנו לשמור אותו, ואינו נוסף למצבת החיילים או לצי הרכבים.
          </p>

          {externalOpen ? (
            <div className="rounded-xl border border-violet-300 bg-panel p-3 space-y-3">
              {directory.people.length > 0 ? (
                <Labelled label="בחירה מרשימת אנשי החוץ השמורים">
                  <select
                    value={externalDirectoryId}
                    onChange={(event) => fillFromDirectory(event.target.value)}
                    aria-label="בחירה מרשימת אנשי החוץ השמורים"
                    className={fieldClass}
                  >
                    <option value="">— הזנה ידנית —</option>
                    {directory.people.map((record) => (
                      <option key={record.id} value={record.id}>
                        {describeExternalPersonRecord(record)}
                      </option>
                    ))}
                  </select>
                </Labelled>
              ) : null}

              <Labelled label="תפקיד במשימה">
                <div className="flex flex-wrap gap-1.5">
                  {EXTERNAL_ROLE_OPTIONS.map((role) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => setExternalCrewRole(role)}
                      aria-pressed={externalCrewRole === role}
                      className={[
                        'text-xs px-3 py-1.5 rounded-lg border transition-colors',
                        externalCrewRole === role
                          ? 'border-violet-500 bg-violet-100 text-violet-800 font-bold'
                          : 'border-line bg-canvas text-ink/60',
                      ].join(' ')}
                    >
                      {CREW_ROLE_LABELS[role]}
                    </button>
                  ))}
                </div>
              </Labelled>

              <div className="grid grid-cols-2 gap-3">
                <Labelled label="שם מלא (חובה)">
                  <input
                    value={externalName}
                    onChange={(event) => setExternalName(event.target.value)}
                    aria-label="שם מלא של איש החוץ"
                    required
                    className={fieldClass}
                  />
                </Labelled>
                <Labelled label="מספר אישי / מ.א. (רשות)">
                  <input
                    value={externalNumber}
                    onChange={(event) => setExternalNumber(event.target.value)}
                    data-dir="ltr"
                    className={fieldClass}
                  />
                </Labelled>
                <Labelled label="טלפון (רשות)">
                  <input
                    type="tel"
                    value={externalPhone}
                    onChange={(event) => setExternalPhone(event.target.value)}
                    inputMode="tel"
                    className={fieldClass}
                  />
                </Labelled>
                <Labelled label="תפקיד / גוף (רשות)">
                  <input
                    value={externalRole}
                    onChange={(event) => setExternalRole(event.target.value)}
                    placeholder="לדוגמה: טכנאי מנופים"
                    className={fieldClass}
                  />
                </Labelled>
                <Labelled label="דגם רכב זמני (רשות)">
                  <input
                    value={externalModel}
                    onChange={(event) => setExternalModel(event.target.value)}
                    placeholder="לדוגמה: פורד טרנזיט"
                    className={fieldClass}
                  />
                </Labelled>
                <Labelled label="לוחית רישוי (רשות)">
                  <input
                    value={externalPlate}
                    onChange={(event) => setExternalPlate(event.target.value)}
                    data-dir="ltr"
                    inputMode="numeric"
                    placeholder="12-345-67"
                    className={fieldClass}
                  />
                </Labelled>
              </div>

              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={externalKeep}
                  onChange={(event) => setExternalKeep(event.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-violet-600"
                />
                <span className="min-w-0">
                  <span className="font-medium">{SAVE_EXTERNAL_PERSON_LABEL}</span>
                  <span className="block text-[11px] text-ink/50">
                    יישמר בקטגוריית "אנשי חוץ" בלבד. ללא סימון — הפרטים נשמרים במשימה זו בלבד.
                  </span>
                </span>
              </label>

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setExternalOpen(false)}
                  className="h-9 px-3 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
                >
                  ביטול
                </button>
                <button
                  type="button"
                  onClick={addExternalPerson}
                  className="h-9 px-4 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-700"
                >
                  הוספה
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => openExternalForm('passenger')}
              className="h-9 px-3 rounded-xl border border-violet-300 bg-panel text-sm text-violet-700 hover:bg-violet-100 flex items-center gap-1.5"
            >
              <UserPlus size={15} aria-hidden />
              + הוסף איש חוץ / זמני
            </button>
          )}
        </section>

        <Labelled label="הערת צוות">
          <textarea
            value={teamNote}
            onChange={(event) => setTeamNote(event.target.value)}
            rows={2}
            placeholder="לדוגמה: יציאה מהשער הצפוני, ציוד כבר עמוס"
            className={areaClass}
          />
        </Labelled>

        {error ? (
          <p className="text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>

    {overrideOpen ? (
      <OverrideDialog
        conflicts={openConflicts}
        task={task}
        managerId={managerId}
        onCancel={() => setOverrideOpen(false)}
        onConfirm={(granted) => {
          setOverrides((current) => [...current, ...granted]);
          setOverrideOpen(false);
          setError(null);
        }}
      />
    ) : null}
    </>
  );
}

/** The button that opens this modal, so both hosts offer the same action. */
export function AddTeamButton({ onClick, label }: { onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-10 px-4 rounded-xl border border-brand-300 bg-brand-50 text-brand-700 text-sm font-medium hover:bg-brand-100 flex items-center gap-1.5"
    >
      <Plus size={16} aria-hidden />
      {label ?? 'הוסף רכב וצוות'}
    </button>
  );
}
